// frontend/src/pages/properties/PropertyListPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiSearch, FiMapPin, FiUpload, FiVideo, FiX } from 'react-icons/fi';
import { propertyAPI } from '../../services/api';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import Modal from '../../components/shared/Modal';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// ==================== UTILITAIRE ====================
const getFileUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/uploads/')) {
    return `${process.env.REACT_APP_STORAGE_URL || 'http://localhost:5000'}${url}`;
  }
  return url;
};

const PropertyListPage = () => {
  const [properties, setProperties] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [types, setTypes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [filters, setFilters] = useState({ search: '', type_id: '', transaction_type: '' });
  const [form, setForm] = useState({ 
    title: '', 
    type_id: '', 
    status_id: '',
    transaction_type: 'rent', 
    city: '', 
    price: '', 
    surface_area: '', 
    num_rooms: '', 
    num_bedrooms: '', 
    description: '', 
    address: '', 
    latitude: '', 
    longitude: '' 
  });
  const [formErrors, setFormErrors] = useState([]);
  
  // États pour les uploads
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [videoFiles, setVideoFiles] = useState([]);
  const [videoPreviews, setVideoPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('grid');

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.type_id) params.type_id = filters.type_id;
      if (filters.transaction_type) params.transaction_type = filters.transaction_type;
      const { data } = await propertyAPI.list(params);
      setProperties(data.data || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProperties();
    // Charger les types et statuses
    const loadOptions = async () => {
      try {
        const [typesRes, statusesRes] = await Promise.all([
          propertyAPI.getTypes(),
          propertyAPI.getStatuses()
        ]);
        setTypes(typesRes.data || []);
        setStatuses(statusesRes.data || []);
        console.log('📋 Types disponibles:', typesRes.data.map(t => ({ id: t.id, name: t.name })));
        console.log('📋 Statuses disponibles:', statusesRes.data.map(s => ({ id: s.id, name: s.name })));
      } catch (error) {
        console.error('Error loading options:', error);
      }
    };
    loadOptions();
  }, []);

  useEffect(() => { 
    fetchProperties(); 
  }, [filters]);

  // Gestion des images
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    setImageFiles(prev => [...prev, ...files]);
    
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setImagePreviews(prev => [...prev, ...newPreviews]);
  };

  const removeImage = (index) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
    URL.revokeObjectURL(imagePreviews[index]);
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Gestion des vidéos
  // const handleVideoSelect = (e) => {
  //   const files = Array.from(e.target.files);
  //   setVideoFiles(prev => [...prev, ...files]);
    
  //   const newPreviews = files.map(file => URL.createObjectURL(file));
  //   setVideoPreviews(prev => [...prev, ...newPreviews]);
  // };
  // frontend/src/pages/properties/PropertyListPage.js
const handleVideoSelect = (e) => {
  const files = Array.from(e.target.files);
  
  // Vérifier la taille des fichiers
  const MAX_SIZE = 500 * 1024 * 1024; // 500MB
  const validFiles = [];
  const invalidFiles = [];
  
  for (const file of files) {
    if (file.size > MAX_SIZE) {
      invalidFiles.push(file);
      alert(`Le fichier ${file.name} est trop volumineux (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum: 500MB`);
    } else {
      validFiles.push(file);
    }
  }
  
  if (validFiles.length === 0) {
    return;
  }
  
  setVideoFiles(prev => [...prev, ...validFiles]);
  
  const newPreviews = validFiles.map(file => URL.createObjectURL(file));
  setVideoPreviews(prev => [...prev, ...newPreviews]);
};

  const removeVideo = (index) => {
    setVideoFiles(prev => prev.filter((_, i) => i !== index));
    URL.revokeObjectURL(videoPreviews[index]);
    setVideoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setUploading(true);
    setFormErrors([]);
    
    try {
      // Nettoyer et préparer les données
      const payload = { 
        title: form.title || '',
        type_id: form.type_id || null,
        status_id: form.status_id || null,
        transaction_type: form.transaction_type || 'rent',
        city: form.city || null,
        price: form.price ? parseFloat(form.price) : null,
        surface_area: form.surface_area ? parseFloat(form.surface_area) : null,
        num_rooms: form.num_rooms ? parseInt(form.num_rooms) : null,
        num_bedrooms: form.num_bedrooms ? parseInt(form.num_bedrooms) : null,
        description: form.description || null,
        address: form.address || null,
        latitude: form.latitude ? parseFloat(form.latitude) : null,
        longitude: form.longitude ? parseFloat(form.longitude) : null,
        // Champs par défaut
        is_furnished: false,
        is_available: true,
        currency: 'XOF',
        is_multi_unit: !!form.is_multi_unit,
      };

      console.log('📝 Envoi des données:', JSON.stringify(payload, null, 2));

      // Vérifier que le titre est présent
      if (!payload.title) {
        setFormErrors([{ field: 'title', message: 'Le titre est requis' }]);
        setUploading(false);
        return;
      }

      const { data } = await propertyAPI.create(payload);
      const propertyId = data.id;
      console.log('✅ Bien créé:', data);

      // 2. Upload des images
      if (imageFiles.length > 0) {
        const imageFormData = new FormData();
        for (let file of imageFiles) {
          imageFormData.append('images', file);
        }
        await propertyAPI.uploadImages(propertyId, imageFormData);
        console.log(`✅ ${imageFiles.length} images uploadées`);
      }

      // 3. Upload des vidéos
      if (videoFiles.length > 0) {
        const videoFormData = new FormData();
        for (let file of videoFiles) {
          videoFormData.append('videos', file);
        }
        await propertyAPI.uploadVideos(propertyId, videoFormData);
        console.log(`✅ ${videoFiles.length} vidéos uploadées`);
      }

      setShowModal(false);
      resetForm();
      fetchProperties();
      if (payload.is_multi_unit) {
        navigate(`/properties/${propertyId}?action=add_unit`);
      } else {
        navigate(`/properties/${propertyId}`);
      }
    } catch (error) {
      console.error('❌ Create error:', error);
      console.error('📝 Response data:', error.response?.data);
      
      if (error.response?.data?.details) {
        setFormErrors(error.response.data.details);
        // Afficher les erreurs dans un alert pour faciliter le débogage
        const errorMessages = error.response.data.details.map(d => 
          `${d.field || 'champ'}: ${d.message}`
        ).join('\n');
        alert(`❌ Erreurs de validation:\n${errorMessages}`);
      } else {
        alert(error.response?.data?.error || 'Erreur lors de la création du bien');
      }
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setForm({ 
      title: '', 
      type_id: '', 
      status_id: '',
      transaction_type: 'rent', 
      city: '', 
      price: '', 
      surface_area: '', 
      num_rooms: '', 
      num_bedrooms: '', 
      description: '', 
      address: '', 
      latitude: '', 
      longitude: '' 
    });
    setFormErrors([]);
    // Nettoyer les previews
    imagePreviews.forEach(preview => URL.revokeObjectURL(preview));
    videoPreviews.forEach(preview => URL.revokeObjectURL(preview));
    setImageFiles([]);
    setImagePreviews([]);
    setVideoFiles([]);
    setVideoPreviews([]);
  };

  const closeModal = () => {
    if (!uploading) {
      setShowModal(false);
      resetForm();
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>Biens immobiliers ({total})</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <FiPlus /> Ajouter un bien
          </button>
          <button className={`btn btn-sm ${viewMode === 'map' ? 'btn-primary' : 'btn-secondary'}`} 
                  onClick={() => setViewMode(viewMode === 'grid' ? 'map' : 'grid')}>
            <FiMapPin /> {viewMode === 'grid' ? 'Vue carte' : 'Vue liste'}
          </button>
        </div>
      </div>

      <div className="filters">
        <div style={{ position: 'relative', flex: 1, maxWidth: 300 }}>
          <FiSearch style={{ position: 'absolute', left: 12, top: 11, color: 'var(--text-secondary)' }} />
          <input className="form-control" style={{ paddingLeft: 36 }} placeholder="Rechercher..." 
                 value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })} />
        </div>
        <select className="form-control" style={{ width: 'auto' }} value={filters.type_id} 
                onChange={e => setFilters({ ...filters, type_id: e.target.value })}>
          <option value="">Tous les types</option>
          {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select className="form-control" style={{ width: 'auto' }} value={filters.transaction_type} 
                onChange={e => setFilters({ ...filters, transaction_type: e.target.value })}>
          <option value="">Toutes transactions</option>
          <option value="rent">Location</option>
          <option value="sale">Vente</option>
        </select>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : (
        <>
          <div className="property-grid">
            {properties.map(p => (
              <div key={p.id} className="property-card" onClick={() => navigate(`/properties/${p.id}`)}>
                <div className="property-card-image">
                  {p.primary_image ? (
                    <img 
                      src={getFileUrl(p.primary_image)} 
                      alt={p.title}
                      onError={(e) => {
                        e.target.src = '/placeholder-image.jpg';
                        e.target.onerror = null;
                      }}
                    />
                  ) : (
                    <FiMapPin />
                  )}
                </div>
                <div className="property-card-body">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <h3>{p.title}</h3>
                    {p.status_name && <span className="badge" style={{ background: p.status_color + '20', color: p.status_color }}>{p.status_name}</span>}
                  </div>
                  <div className="location"><FiMapPin size={12} /> {p.city || 'N/A'} {p.neighborhood ? `- ${p.neighborhood}` : ''}</div>
                  <div className="price">{p.price ? `${parseInt(p.price).toLocaleString()} XOF` : 'Prix non défini'}{p.transaction_type === 'rent' ? '/mois' : ''}</div>
                  <div className="meta">
                    {p.is_multi_unit ? (
                      <span className="badge" style={{ background: '#8b5cf620', color: '#8b5cf6', fontSize: 11 }}>
                        Immeuble ({p.units_count || 0} unités)
                      </span>
                    ) : (
                      p.type_name && <span>{p.type_name}</span>
                    )}
                    {p.surface_area && <span>{p.surface_area} m²</span>}
                    {p.num_rooms && <span>{p.num_rooms} pièces</span>}
                  </div>
                </div>
              </div>
            ))}
            {properties.length === 0 && <div className="empty-state"><h3>Aucun bien trouvé</h3><p>Ajoutez votre premier bien immobilier</p></div>}
          </div>

          {viewMode === 'map' && (
            <div style={{ height: 500, borderRadius: 12, overflow: 'hidden', marginTop: 16 }}>
              <MapContainer center={[12.6392, -8.0029]} zoom={12} style={{ height: '100%', width: '100%' }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap contributors' />
                {properties.filter(p => p.latitude && p.longitude).map(p => (
                  <Marker key={p.id} position={[parseFloat(p.latitude), parseFloat(p.longitude)]}>
                    <Popup>
                      <strong>{p.title}</strong><br />
                      {p.type_name && <span>{p.type_name} — </span>}{p.city}<br />
                      {p.price ? `${parseInt(p.price).toLocaleString()} XOF` : 'Prix non défini'}<br />
                      <button style={{ marginTop: 4, cursor: 'pointer' }} onClick={() => navigate(`/properties/${p.id}`)}>Voir détails</button>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          )}
        </>
      )}

      {/* Modal d'ajout de bien avec uploads d'images et vidéos */}
      <Modal isOpen={showModal} onClose={closeModal} title="Ajouter un bien" size="lg">
        <form onSubmit={handleCreate}>
          {/* Affichage des erreurs */}
          {formErrors.length > 0 && (
            <div style={{ background: '#fee2e2', color: '#dc2626', padding: 12, borderRadius: 8, marginBottom: 16 }}>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {formErrors.map((err, idx) => (
                  <li key={idx}>{err.field ? `${err.field}: ` : ''}{err.message}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="form-group" style={{ marginBottom: 20 }}>
            <label className="form-checkbox" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', background: 'var(--bg-secondary)', padding: '12px 16px', borderRadius: 8, border: '1px solid var(--border)' }}>
              <input 
                type="checkbox" 
                checked={form.is_multi_unit || false}
                onChange={e => setForm({...form, is_multi_unit: e.target.checked})}
                style={{ width: 18, height: 18 }}
              />
              <strong>Cette propriété contient plusieurs unités (appartements, bureaux, etc.)</strong>
            </label>
          </div>

          <div className="form-group">
            <label>Titre <span style={{ color: 'red' }}>*</span></label>
            <input 
              className="form-control" 
              value={form.title} 
              onChange={e => setForm({ ...form, title: e.target.value })} 
              required 
              placeholder="ex: Appartement de luxe à Bamako"
            />
          </div>
          
          <div className="grid-2">
            <div className="form-group">
              <label>Type de bien</label>
              <select className="form-control" value={form.type_id} onChange={e => setForm({ ...form, type_id: e.target.value })}>
                <option value="">Sélectionner un type</option>
                {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Statut</label>
              <select className="form-control" value={form.status_id} onChange={e => setForm({ ...form, status_id: e.target.value })}>
                <option value="">Sélectionner un statut</option>
                {statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          
          <div className="grid-2">
            <div className="form-group">
              <label>Prix (XOF) <span style={{ color: 'red' }}>*</span></label>
              <input 
                type="number" 
                className="form-control" 
                value={form.price} 
                onChange={e => setForm({ ...form, price: e.target.value })}
                placeholder="ex: 150000"
                required 
              />
            </div>
            <div className="form-group">
              <label>Type de transaction</label>
              <select className="form-control" value={form.transaction_type} onChange={e => setForm({ ...form, transaction_type: e.target.value })}>
                <option value="rent">Location</option>
                <option value="sale">Vente</option>
                <option value="both">Les deux</option>
              </select>
            </div>
          </div>
          
          <div className="grid-2">
            <div className="form-group">
              <label>Surface (m²)</label>
              <input 
                type="number" 
                className="form-control" 
                value={form.surface_area} 
                onChange={e => setForm({ ...form, surface_area: e.target.value })}
                placeholder="ex: 120"
              />
            </div>
            <div className="form-group">
              <label>Nombre de pièces</label>
              <input 
                type="number" 
                className="form-control" 
                value={form.num_rooms} 
                onChange={e => setForm({ ...form, num_rooms: e.target.value })}
                placeholder="ex: 3"
              />
            </div>
          </div>
          
          <div className="grid-2">
            <div className="form-group">
              <label>Ville</label>
              <input 
                className="form-control" 
                value={form.city} 
                onChange={e => setForm({ ...form, city: e.target.value })}
                placeholder="ex: Bamako"
              />
            </div>
            <div className="form-group">
              <label>Adresse</label>
              <input 
                className="form-control" 
                value={form.address} 
                onChange={e => setForm({ ...form, address: e.target.value })}
                placeholder="ex: Quartier du Fleuve, Rue 310"
              />
            </div>
          </div>
          
          <div className="grid-2">
            <div className="form-group">
              <label>Latitude GPS</label>
              <input 
                type="number" 
                step="any" 
                className="form-control" 
                placeholder="ex: 12.6392" 
                value={form.latitude} 
                onChange={e => setForm({ ...form, latitude: e.target.value })} 
              />
            </div>
            <div className="form-group">
              <label>Longitude GPS</label>
              <input 
                type="number" 
                step="any" 
                className="form-control" 
                placeholder="ex: -8.0029" 
                value={form.longitude} 
                onChange={e => setForm({ ...form, longitude: e.target.value })} 
              />
            </div>
          </div>
          
          <div className="form-group">
            <label>Description</label>
            <textarea 
              className="form-control" 
              rows="3" 
              value={form.description} 
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="Décrivez le bien en détail..."
            />
          </div>

          {/* Section Upload Images */}
          <div className="form-group">
            <label>Images</label>
            <div style={{ border: '2px dashed var(--border)', borderRadius: 8, padding: 16, textAlign: 'center', cursor: 'pointer' }}
                 onClick={() => document.getElementById('imageInput').click()}>
              <FiUpload size={24} style={{ marginBottom: 8, color: 'var(--primary)' }} />
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
                Cliquez pour sélectionner des images (JPG, PNG, WEBP)
              </p>
              <input id="imageInput" type="file" multiple accept="image/*" 
                     onChange={handleImageSelect} style={{ display: 'none' }} />
            </div>
            
            {imagePreviews.length > 0 && (
              <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                {imagePreviews.map((preview, idx) => (
                  <div key={idx} style={{ position: 'relative', width: 80, height: 80 }}>
                    <img src={preview} alt={`Preview ${idx}`} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
                    <button type="button" onClick={() => removeImage(idx)}
                            style={{ position: 'absolute', top: -8, right: -8, background: 'red', color: 'white', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FiX size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Section Upload Vidéos */}
          <div className="form-group">
            <label>Vidéos</label>
            <div style={{ border: '2px dashed var(--border)', borderRadius: 8, padding: 16, textAlign: 'center', cursor: 'pointer' }}
                 onClick={() => document.getElementById('videoInput').click()}>
              <FiVideo size={24} style={{ marginBottom: 8, color: 'var(--primary)' }} />
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
                Cliquez pour sélectionner des vidéos (MP4, WEBM, MOV)
              </p>
              <input id="videoInput" type="file" multiple accept="video/*" 
                     onChange={handleVideoSelect} style={{ display: 'none' }} />
            </div>
            
            {videoPreviews.length > 0 && (
              <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                {videoPreviews.map((preview, idx) => (
                  <div key={idx} style={{ position: 'relative', width: 120, height: 80, background: '#000', borderRadius: 6, overflow: 'hidden' }}>
                    <video src={preview} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <button type="button" onClick={() => removeVideo(idx)}
                            style={{ position: 'absolute', top: -8, right: -8, background: 'red', color: 'white', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FiX size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
            <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={uploading}>
              Annuler
            </button>
            <button type="submit" className="btn btn-primary" disabled={uploading}>
              {uploading ? 'Création en cours...' : 'Créer le bien'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default PropertyListPage;